import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, resolveDynamicComponent as _resolveDynamicComponent, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["disabled", "aria-expanded", "aria-label", "data-tippy-content", "data-tippy-delay"]
const _hoisted_2 = ["disabled", "onClick"]
const _hoisted_3 = {
  key: 0,
  class: "material-icons"
}

import { Teleport, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { onDocumentModalEvent } from '@/components/modal-events';
import type { OverflowMenuOption } from './OverflowMenu';


export default /*@__PURE__*/_defineComponent({
  __name: 'OverflowMenu',
  props: {
    options: {},
    align: { default: 'right' },
    buttonLabel: { default: 'Open menu' },
    triggerClass: { default: '' },
    triggerTooltip: { default: '' },
    closeOnSelect: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    portal: { type: Boolean, default: false },
    placement: { default: 'bottom' },
    offset: { default: 6 }
  },
  emits: ["select", "open", "close"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const isOpen = ref(false);
const rootRef = ref<HTMLElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const panelStyle = ref<Record<string, string>>({});
let removeModalHideListener: (() => void) | null = null;

function updatePanelPosition(): void {
  if (!props.portal || !rootRef.value || !panelRef.value) {
    return;
  }

  const triggerRect = rootRef.value.getBoundingClientRect();
  const panelRect = panelRef.value.getBoundingClientRect();
  const panelWidth = panelRect.width || panelRef.value.offsetWidth || 176;
  const panelHeight = panelRect.height || panelRef.value.offsetHeight || 0;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const edgeMargin = 12;

  const left = props.align === 'left'
    ? Math.min(Math.max(edgeMargin, triggerRect.left), viewportWidth - panelWidth - edgeMargin)
    : Math.min(Math.max(edgeMargin, triggerRect.right - panelWidth), viewportWidth - panelWidth - edgeMargin);

  const preferredTop = props.placement === 'top'
    ? triggerRect.top - panelHeight - props.offset
    : triggerRect.bottom + props.offset;

  const fallbackTop = props.placement === 'top'
    ? triggerRect.bottom + props.offset
    : triggerRect.top - panelHeight - props.offset;

  const fitsPreferred = props.placement === 'top'
    ? preferredTop >= edgeMargin
    : preferredTop + panelHeight <= viewportHeight - edgeMargin;

  const maxTop = Math.max(edgeMargin, viewportHeight - panelHeight - edgeMargin);
  const top = fitsPreferred
    ? preferredTop
    : Math.min(
      Math.max(edgeMargin, fallbackTop),
      maxTop
    );

  panelStyle.value = {
    position: 'fixed',
    left: `${Math.round(left)}px`,
    right: 'auto',
    top: `${Math.round(top)}px`,
    bottom: 'auto',
    zIndex: '2600',
    width: 'max-content',
    minWidth: '176px',
    maxWidth: `calc(100vw - ${edgeMargin * 2}px)`,
    maxHeight: `calc(100vh - ${edgeMargin * 2}px)`,
    overflowY: 'auto'
  };
}

function closeMenu(): void {
  if (!isOpen.value) {
    return;
  }

  isOpen.value = false;
  emit('close');
}

function openMenu(): void {
  if (isOpen.value) {
    return;
  }

  isOpen.value = true;
  emit('open');
  void nextTick(() => {
    updatePanelPosition();
    window.requestAnimationFrame(() => {
      updatePanelPosition();
    });
  });
}

function toggleMenu(): void {
  if (props.disabled) {
    return;
  }
  if (isOpen.value) {
    closeMenu();
    return;
  }

  openMenu();
}

function handleSelect(key: string): void {
  emit('select', key);
  if (props.closeOnSelect) {
    closeMenu();
  }
}

function handleDocumentPointerDown(event: PointerEvent): void {
  if (!rootRef.value) {
    return;
  }

  const target = event.target as Node | null;
  if (target && !rootRef.value.contains(target) && !panelRef.value?.contains(target)) {
    closeMenu();
  }
}

function handleDocumentKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    closeMenu();
  }
}

function handleViewportChange(): void {
  if (isOpen.value) {
    updatePanelPosition();
  }
}

function handleModalHide(): void {
  closeMenu();
}

watch(() => props.disabled, (isDisabled) => {
  if (isDisabled) {
    closeMenu();
  }
});

onMounted(() => {
  document.addEventListener('pointerdown', handleDocumentPointerDown);
  document.addEventListener('keydown', handleDocumentKeydown);
      removeModalHideListener = onDocumentModalEvent('hide.bs.modal', handleModalHide);
  window.addEventListener('resize', handleViewportChange, true);
  window.addEventListener('scroll', handleViewportChange, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleDocumentPointerDown);
  document.removeEventListener('keydown', handleDocumentKeydown);
  removeModalHideListener?.();
  removeModalHideListener = null;
  window.removeEventListener('resize', handleViewportChange, true);
  window.removeEventListener('scroll', handleViewportChange, true);
});

__expose({
  closeMenu,
  openMenu,
  panelRef,
  rootRef
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "rootRef",
    ref: rootRef,
    class: "ui-overflow-menu"
  }, [
    _createElementVNode("button", {
      type: "button",
      disabled: __props.disabled,
      class: _normalizeClass(['ui-overflow-menu__trigger', __props.triggerClass, { 'is-open': isOpen.value }]),
      "aria-expanded": isOpen.value ? 'true' : 'false',
      "aria-label": __props.buttonLabel,
      "data-tippy-content": __props.triggerTooltip || undefined,
      "data-tippy-placement": "top",
      "data-tippy-delay": __props.triggerTooltip ? '2000,0' : undefined,
      onClick: _withModifiers(toggleMenu, ["stop"])
    }, [
      _renderSlot(_ctx.$slots, "trigger", {}, () => [
        _cache[0] || (_cache[0] = _createElementVNode("i", { class: "material-icons" }, "more_vert", -1 /* CACHED */))
      ])
    ], 10 /* CLASS, PROPS */, _hoisted_1),
    (_openBlock(), _createBlock(_resolveDynamicComponent(__props.portal ? Teleport : 'div'), _normalizeProps(_guardReactiveProps(__props.portal ? { to: 'body' } : { class: 'ui-overflow-menu__inline-layer' })), {
      default: _withCtx(() => [
        (isOpen.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              ref_key: "panelRef",
              ref: panelRef,
              class: _normalizeClass([
          'ui-overflow-menu__panel',
          { 'ui-overflow-menu__panel--fixed': __props.portal },
          __props.portal ? '' : `ui-overflow-menu__panel--${__props.align}`
        ]),
              style: _normalizeStyle(__props.portal ? panelStyle.value : undefined),
              role: "menu"
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.options, (option) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: option.key,
                  type: "button",
                  role: "menuitem",
                  disabled: Boolean(option.disabled),
                  class: _normalizeClass([
            'ui-overflow-menu__item',
            option.className,
            { 'is-danger': option.tone === 'danger' }
          ]),
                  onClick: _withModifiers(($event: any) => (handleSelect(option.key)), ["stop"])
                }, [
                  (option.icon)
                    ? (_openBlock(), _createElementBlock("i", _hoisted_3, _toDisplayString(option.icon), 1 /* TEXT */))
                    : _createCommentVNode("v-if", true),
                  _createElementVNode("span", null, _toDisplayString(option.label), 1 /* TEXT */)
                ], 10 /* CLASS, PROPS */, _hoisted_2))
              }), 128 /* KEYED_FRAGMENT */))
            ], 6 /* CLASS, STYLE */))
          : _createCommentVNode("v-if", true)
      ]),
      _: 1 /* STABLE */
    }, 16 /* FULL_PROPS */))
  ], 512 /* NEED_PATCH */))
}
}

})